import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import moment from 'moment';
import _ from 'lodash';

import {
  getOrderStatus,
  getPollingEditOrder,
  isLoadingOrderSelector,
  selectedOrderSelector,
} from 'src/store/orders';

import { PageLoader, WhiteCard } from 'src/components';

import { SuccessIcon, WarningIcon } from 'src/assets';
import './exchangeStatus.scss';

export const ExchangeStatus = () => {
  const { t, i18n } = useTranslation('exchangeStatus');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const selectedOrder = useSelector(selectedOrderSelector);
  const isLoading = useSelector(isLoadingOrderSelector);

  useEffect(() => {
    const payload = {
      id: params?.id,
      navigate,
    };

    dispatch(getOrderStatus(payload));
  }, [dispatch, navigate, params?.id, i18n.language]);

  useEffect(() => {
    if (!_.isEmpty(selectedOrder)) {
      const payload = {
        id: params?.id,
        navigate,
      };

      const interval = setInterval(() => {
        dispatch(getPollingEditOrder(payload));
      }, 10000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [dispatch, navigate, params?.id, selectedOrder]);

  const renderStyleByStatus = useCallback((status) => {
    const style = classNames({
      pending: status?.toLowerCase() === 'pending' || status?.toLowerCase() === 'partial_payment',
      success: status?.toLowerCase() === 'paid' || status?.toLowerCase() === 'complete',
      rejected: status?.toLowerCase() === 'cancel',
    });

    return style;
  }, []);

  const renderItem = useCallback(
    (text, value, key) => {
      return (
        <div className="exchange-status__item" key={key}>
          <div className="exchange-status__item__text">{key ? text : t(text)}</div>
          <h5>{value}</h5>
        </div>
      );
    },
    [t],
  );

  const headerTitle = {
    PENDING: t('exchangeStatuses:PENDING'),
    PAID: t('exchangeStatuses:PAID'),
    COMPLETE: t('exchangeStatuses:COMPLETE'),
    CANCEL: t('exchangeStatuses:CANCEL'),
    PARTIAL_PAYMENT: t('exchangeStatuses:PARTIAL_PAYMENT'),
  };

  const bodyTitle = {
    PENDING: t('statuses:PENDING'),
    PAID: t('statuses:PAID'),
    COMPLETE: t('statuses:COMPLETE'),
    CANCEL: t('statuses:CANCEL'),
    PARTIAL_PAYMENT: t('statuses:PARTIAL_PAYMENT'),
  };

  const given = selectedOrder?.given;
  const received = selectedOrder?.received;

  return (
    <div className="exchange-status">
      {isLoading ? (
        <PageLoader />
      ) : (
        <WhiteCard>
          <h2>
            {t('title')} {selectedOrder?.id}
          </h2>
          <div className="exchange-status__content">
            <WhiteCard>
              <h5 className={renderStyleByStatus(selectedOrder?.status)}>
                {['paid', 'complete', 'cancel'].includes(selectedOrder?.status) && (
                  <>
                    {(selectedOrder?.status === 'paid' || selectedOrder?.status === 'complete') && (
                      <img src={SuccessIcon} alt={SuccessIcon} />
                    )}
                    {selectedOrder?.status === 'cancel' && (
                      <img src={WarningIcon} alt={WarningIcon} />
                    )}
                  </>
                )}
                {headerTitle[selectedOrder?.status]}
              </h5>
              {renderItem(
                'viewExchange:created',
                moment(selectedOrder.created_at).format('DD.MM.YYYY, HH:mm'),
              )}
              {renderItem(
                'viewExchange:status',
                <span className={renderStyleByStatus(selectedOrder?.status)}>
                  {bodyTitle[selectedOrder?.status]}
                </span>,
              )}
              {renderItem(
                'createExchange:exchangeRate',
                `${given?.rate_given_currency} ${given?.code} → ${received?.rate_received_currency} ${received?.code}`,
              )}
              {/* {renderItem('exchangeBonus', mockedData.exchangeBonus)} */}
              {renderItem(
                'createExchange:give',
                `${given?.name}, ${selectedOrder?.given_sum} ${given?.code}`,
              )}
              {renderItem(
                'createExchange:receive',
                `${received?.name}, ${selectedOrder?.received_sum} ${received?.code}`,
              )}
              {selectedOrder?.fields?.map((item) =>
                renderItem(item?.field_translate, item?.field_name, item?.field),
              )}
            </WhiteCard>
          </div>
        </WhiteCard>
      )}
    </div>
  );
};
